import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Switch, Modal, Form, Input, Select, message, Space, Typography, Tag } from 'antd';
import { PlusOutlined, EditOutlined, SettingOutlined } from '@ant-design/icons';
import { baseurl } from '../../../components/lib/settings';

const { Title } = Typography;
const { Option } = Select;

const AdminGerirEdicoes = () => {
    const [edicoes, setEdicoes] = useState([]);
    const [delegacoes, setDelegacoes] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEdicao, setEditingEdicao] = useState(null);
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        fetchData();
        fetchDelegacoes();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await baseurl.get('v3/ngoma/manage_editions/');
            setEdicoes(response.data);
        } catch (error) {
            message.error("Erro ao carregar edições.");
        } finally {
            setLoading(false);
        }
    };

    const fetchDelegacoes = async () => {
        try {
            const response = await baseurl.get('v3/get_delegation_list/');
            setDelegacoes(response.data);
        } catch (error) {
            message.error("Erro ao carregar delegações.");
        }
    };

    const handleToggleActive = async (checked, record) => {
        try {
            await baseurl.put(`v3/ngoma/manage_editions/${record.id}/`, {
                is_active: checked
            });
            message.success(`Edição ${checked ? 'activada' : 'desactivada'} com sucesso.`);
            fetchData();
        } catch (error) {
            const errMsg = error.response?.data?.error || "Erro ao alterar o estado da edição.";
            message.error(errMsg);
            fetchData(); // reload to reset the switch state visually
        }
    };

    const handleOpenModal = (record = null) => {
        setEditingEdicao(record);
        if (record) {
            form.setFieldsValue({
                description: record.description,
                delegacao_id: record.delegacao_id,
                voting_pricing: record.voting_pricing,
                is_active: record.is_active,
                is_disbursed: record.is_disbursed
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                voting_pricing: 10,
                is_active: true,
                is_disbursed: false
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            if (editingEdicao) {
                await baseurl.put(`v3/ngoma/manage_editions/${editingEdicao.id}/`, values);
                message.success("Edição atualizada com sucesso!");
            } else {
                await baseurl.post('v3/ngoma/manage_editions/', values);
                message.success("Edição criada com sucesso!");
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            const errMsg = error.response?.data?.error || "Erro ao guardar a edição.";
            message.error(errMsg);
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns = [
        {
            title: 'Nome da Edição',
            dataIndex: 'description',
            key: 'description',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Delegação',
            dataIndex: 'delegacao_desc',
            key: 'delegacao_desc',
        },
        {
            title: 'Preço/Voto',
            dataIndex: 'voting_pricing',
            key: 'voting_pricing',
            render: (val) => `${val || 0} MT`
        },
        {
            title: 'Estado',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active, record) => (
                <Space>
                    <Switch 
                        checked={active} 
                        onChange={(checked) => handleToggleActive(checked, record)} 
                        checkedChildren="Ativa" 
                        unCheckedChildren="Inativa"
                    />
                    {active ? <Tag color="green">Activa</Tag> : <Tag color="red">Desactivada</Tag>}
                </Space>
            )
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_, record) => (
                <Button 
                    type="primary" 
                    icon={<EditOutlined />} 
                    size="small" 
                    onClick={() => handleOpenModal(record)}
                >
                    Editar
                </Button>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={
                    <Space>
                        <SettingOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        <Title level={3} style={{ margin: 0 }}>Gestão de Edições</Title>
                    </Space>
                }
                extra={
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                        Nova Edição
                    </Button>
                }
                style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
            >
                <Table 
                    columns={columns} 
                    dataSource={edicoes} 
                    rowKey="id" 
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={editingEdicao ? "Editar Edição" : "Nova Edição"}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ is_active: true, voting_pricing: 10, is_disbursed: false }}
                >
                    <Form.Item
                        name="description"
                        label="Nome da Edição"
                        rules={[{ required: true, message: 'Insira o nome da edição' }]}
                    >
                        <Input placeholder="Ex: NGOMA 2024" size="large" />
                    </Form.Item>
                    
                    <Form.Item
                        name="delegacao_id"
                        label="Delegação Associada"
                        rules={[{ required: true, message: 'Selecione a delegação' }]}
                    >
                        <Select placeholder="Selecione..." size="large" disabled={!!editingEdicao}>
                            {delegacoes.map(del => (
                                <Option key={del.id} value={del.id}>{del.description}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="voting_pricing"
                        label="Preço por Voto (MZN)"
                        rules={[{ required: true, message: 'Insira o valor por voto' }]}
                    >
                        <Input type="number" size="large" />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label="Ativar imediatamente?"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                            <Button type="primary" htmlType="submit" loading={submitLoading}>
                                {editingEdicao ? "Guardar Alterações" : "Criar Edição"}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AdminGerirEdicoes;
